import { Avatar, type AvatarTone, FloatPanel } from "@/features/studio/theme";
import { resolveMediaUrl } from "@/lib/utils";
import type { CommentDto } from "../types";
import { CommentAttachmentPreview } from "./comment-attachment-preview";

function initials(name: string): string {
	return name
		.split(" ")
		.map((part) => part[0])
		.join("")
		.slice(0, 2)
		.toUpperCase();
}

export function CommentThread({
	comments,
}: {
	comments: CommentDto[];
}): React.ReactElement {
	return (
		<div className="space-y-4">
			{comments.map((comment) => {
				const tone = (
					comment.author.userType === "homeowner" ? "stone" : "amber"
				) as AvatarTone;
				return (
					<FloatPanel key={comment.id} elevation={1} padding={16}>
						<div className="flex gap-4">
							<Avatar
								initials={initials(comment.author.displayName)}
								src={
									comment.author.profilePictureUrl
										? resolveMediaUrl(comment.author.profilePictureUrl)
										: null
								}
								tone={tone}
								size={36}
							/>
							<div className="min-w-0 flex-1">
								<div className="flex flex-wrap items-center gap-2">
									<p
										style={{
											fontFamily: "var(--font-display)",
											fontWeight: 600,
											fontSize: 14.5,
											color: "var(--color-dashboard-text)",
										}}
									>
										{comment.author.displayName}
									</p>
									<span
										style={{
											fontFamily: "var(--font-mono)",
											fontSize: 9,
											textTransform: "uppercase",
											letterSpacing: "0.08em",
											color: "var(--color-dashboard-muted)",
										}}
									>
										{comment.author.userType ?? "member"}
									</span>
									<span style={{ color: "var(--color-dashboard-faint-text)" }}>
										·
									</span>
									<p
										style={{
											fontFamily: "var(--font-body)",
											fontSize: 12,
											color: "var(--color-dashboard-muted)",
										}}
									>
										{new Date(comment.createdAt).toLocaleString("en-IN")}
									</p>
								</div>
								{comment.content && (
									<p
										style={{
											fontFamily: "var(--font-body)",
											fontSize: 14,
											lineHeight: 1.5,
											color: "var(--color-dashboard-muted-dark)",
											marginTop: 6,
											whiteSpace: "pre-wrap",
										}}
										data-sensitive
									>
										{/* data-sensitive: masked in session replay. Shared by
										    homeowner, designer and contractor threads, so this
										    one attribute covers every project conversation.
										    See lib/posthog.ts. */}
										{comment.content}
									</p>
								)}
								{comment.attachment && (
									<div style={{ marginTop: 10 }}>
										<CommentAttachmentPreview attachment={comment.attachment} />
									</div>
								)}
							</div>
						</div>
					</FloatPanel>
				);
			})}
		</div>
	);
}
